import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const ICONS = {
  dashboard: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
    </svg>
  ),
  customers: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  ),
  earnings: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  network: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2m-10 2v-2m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
  statements: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.6a1 1 0 01.7.3l5.4 5.4a1 1 0 01.3.7V19a2 2 0 01-2 2z" />
    </svg>
  ),
  users: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M17 20h5v-2a3 3 0 00-4.356-2.737M9 20H4v-2a3 3 0 014.356-2.737M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM5 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  ),
  inviteCode: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
    </svg>
  ),
  lock: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    </svg>
  ),
  menu: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  ),
  close: (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M6 18L18 6M6 6l12 12" />
    </svg>
  )
};

export default function LayoutV2({ children }) {
  const { user, logout, isAdmin, isOperations, isDirector, isManager } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const primaryMenu = useMemo(() => {
    const base = [{ path: '/dashboard', label: 'Dashboard', icon: ICONS.dashboard }];

    if (isAdmin || isOperations) {
      base.push({ path: '/admin/customers', label: 'All Customers', icon: ICONS.customers });
      base.push({ path: '/admin/invite-codes', label: 'Referral Codes', icon: ICONS.inviteCode });
      base.push({ path: '/admin/users', label: 'Users', icon: ICONS.users });
    }

    if (!isAdmin && !isOperations) {
      base.push({ path: '/customers', label: 'My Customers', icon: ICONS.customers });
      base.push({ path: '/network', label: 'Network', icon: ICONS.network });
      base.push({ path: '/earnings', label: 'Earnings', icon: ICONS.earnings });
    }

    if (isAdmin || isDirector || isManager) {
      base.push({ path: '/statements', label: 'Statements', icon: ICONS.statements });
    }

    base.push({ path: '/change-password', label: 'Change Password', icon: ICONS.lock });
    return base;
  }, [isAdmin, isOperations, isDirector, isManager]);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 1024) {
        setDrawerOpen(false);
      }
    };

    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navContent = (
    <div className="app-sidebar flex h-full flex-col border-r">
      <div className="border-b px-4 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-300/15 text-blue-200">
            <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <circle cx="12" cy="9.5" r="5.5" strokeWidth="1.6" />
              <path d="M5.5 14.5C7.6 17.2 9.9 18.5 12 18.5c2.1 0 4.4-1.3 6.5-4" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
          </div>
          <div className="min-w-0">
            <p className="truncate text-base font-semibold text-slate-50">Octopus</p>
          </div>
        </div>
      </div>

      <nav className="scrollbar-thin flex-1 space-y-1 overflow-y-auto px-2.5 py-4">
        {primaryMenu.map((item) => {
          const active = location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex min-h-[42px] items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-all duration-200 ${
                active
                  ? 'bg-blue-300/14 text-blue-100 shadow-[inset_0_0_0_1px_rgba(168,199,250,0.22)]'
                  : 'text-slate-300 hover:bg-white/[0.04] hover:text-slate-50'
              }`}
            >
              <span className={active ? 'text-blue-200' : 'text-slate-400'}>{item.icon}</span>
              <span className="truncate">{item.label}</span>
            </Link>
          );
        })}
      </nav>

      <div className="border-t px-3.5 py-3.5">
        <button
          type="button"
          onClick={handleLogout}
          className="btn btn-danger w-full justify-center"
        >
          <span>Logout</span>
        </button>
      </div>
    </div>
  );

  return (
    <div className="app-shell min-h-screen lg:flex">
      <aside className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-[272px]">
        <div className="w-full">{navContent}</div>
      </aside>

      <div className="flex min-h-screen flex-1 flex-col lg:ml-[272px]">
        <header className="app-topbar fixed inset-x-0 top-0 z-40 border-b lg:left-[272px]">
          <div className="mx-auto flex h-14 w-full items-center justify-between px-3.5 sm:px-5 lg:px-6">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setDrawerOpen(true)}
                className="btn btn-secondary h-10 w-10 rounded-xl px-0 lg:hidden"
                aria-label="Open navigation"
              >
                {ICONS.menu}
              </button>
              <p className="text-sm font-medium text-slate-100">{user?.displayName || user?.username}</p>
            </div>

            <div className="hidden items-center gap-3 md:flex">
              <p className="text-sm text-slate-200">@{user?.username}</p>
            </div>
          </div>
        </header>

        <main className="page-container">{children}</main>
      </div>

      <div className={`fixed inset-0 z-50 transition-all duration-200 lg:hidden ${drawerOpen ? 'pointer-events-auto' : 'pointer-events-none'}`}>
        <div
          className={`absolute inset-0 bg-black/55 transition-opacity duration-200 ${drawerOpen ? 'opacity-100' : 'opacity-0'}`}
          onClick={() => setDrawerOpen(false)}
        />
        <aside className={`absolute inset-y-0 left-0 w-[88vw] max-w-[320px] transition-transform duration-300 ${drawerOpen ? 'translate-x-0' : '-translate-x-full'}`}>
          <div className="relative h-full">
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="btn btn-secondary absolute right-4 top-4 z-10 h-10 w-10 rounded-full px-0"
              aria-label="Close navigation"
            >
              {ICONS.close}
            </button>
            {navContent}
          </div>
        </aside>
      </div>
    </div>
  );
}
